/**
 * @author Matěj Grabovský <matej.grabovsky@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isObject from 'lodash/isObject';

import Fingerprint2 from 'fingerprintjs2';

/**
 * Generátor informací pro otisk zařízení z informací dostupných ve webovém prohlížeči.
 * @implements {FingerprintManager}
 */
class FingerprintBrowser {
  /**
   * Vytvoří instanci.
   * @param {FingerprintHasher} hasher Objekt pro hashování informací.
   * @returns {FingerprintBrowser} Nová instance.
   */
  constructor(hasher) {
    if (!isObject(hasher)) {
      throw new TypeError(`Invalid fingerprint hasher "${hasher}".`);
    }
    this.hasher = hasher;
  }

  /** @inheritdoc */
  generate() {
    return new Promise((resolve) =>
      resolve(this.getInfo().then((info) => this.hasher.hashObject(info)))
    );
  }

  /**
   * Zjistí seznam informací z prohlížeče, ze kterých lze generovat otisk.
   * @private
   * @returns {Promise.<Object>} Objekt s komponentami otisku.
   * Názvy komponent Fingerprint2 tvoří klíče objektu.
   * Jejich výsledky jsou hodnotami objektu.
   */
  getInfo() {
    return new Promise((resolve) =>
      resolve(
        Fingerprint2.getPromise({
          // Komponenty otisku, které se při generování neberou v potaz.
          excludes: {
            // Hodně záleží na detekčním algoritmu a verzi a nastavení adBlock.
            adBlock: true,
            // Viz `screenResolution`.
            availableScreenResolution: true,
            // Na Windows není spolehlivé.
            enumerateDevices: true,
            // Nepoužíváme Flash.
            fontsFlash: true,
            // Viz `screenResolution`.
            hasLiedResolution: true,
            // Poměr velikosti pixelů závisí na zoomu, který ale detekovat nelze.
            pixelRatio: true,
            // Nezahrnujeme rozlišení, protože se mění při změně velikosti okna prohlížeče.
            screenResolution: true,
            // Mění se během roku např. při letním / zimním čase.
            timezoneOffset: true
          },
          plugins: {
            excludeIE: true
          }
        })
          // Seznam komponent se převede na objekt.
          // Pořadí komponent není zajímavé.
          .then((componentArray) =>
            componentArray.reduce((acc, component) => {
              acc[component.key] = component.value;
              return acc;
            }, {})
          )
      )
    );
  }
}

export default FingerprintBrowser;
